import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatIST } from '@/lib/utils';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import JobWorkLedger from './ExternalProcessing/JobWorkLedger';
import { toast } from 'sonner';
import {
  Search, RefreshCw, CheckCircle2, AlertTriangle, Package, MapPin, Building2, IndianRupee, X,
} from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, TextCell } from '@/components/data';
import PhoneTracker from './ExternalProcessing/PhoneTracker';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * What is out of the building, longest away first.
 *
 * The tracker keeps its tab beside the Job Work Ledger — a movement list and a
 * financial reconciliation really are two shapes, and that is the one case the
 * revamp keeps tabs for. What it loses is the receive dialog: booking something
 * back in is a row action with an optional note typed in place, because the only
 * question being answered is "is it here yet".
 */

const STATUS_TEXT = {
  sent: 'text-blue-600',
  in_progress: 'text-amber-600',
  completed: 'text-emerald-600',
  sent_back: 'text-orange-600',
  received: 'text-slate-400',
};

const STATUS_LABELS = {
  sent: 'Sent',
  in_progress: 'In Progress',
  completed: 'Completed',
  sent_back: 'Sent Back',
  received: 'Received',
};

const DESTINATIONS = [['city_office', 'City Office'], ['vendor', 'Vendor']];

export default function ExternalProcessingPage() {
  const isMobile = useIsMobile();
  const [view, setView] = useState('tracker');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [destination, setDestination] = useState(null);
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [receiving, setReceiving] = useState(null);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (destination) params.destination = destination;
      if (status) params.status = status;
      const res = await api.get('/external-processing/tracker', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load tracker'); }
    setLoading(false);
  }, [search, destination, status, page]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const confirmReceive = async (item) => {
    setSubmitting(true);
    try {
      await api.post('/external-processing/mark-received', {
        tracking_id: item.id,
        notes: receiveNotes,
      });
      toast.success('Marked as received. Item moved to next stage.');
      setReceiving(null);
      setReceiveNotes('');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
    setSubmitting(false);
  };

  const counts = useMemo(() => ({
    city_office: items.filter(i => i.destination_type === 'city_office' && i.status !== 'received').length,
    vendor: items.filter(i => i.destination_type === 'vendor' && i.status !== 'received').length,
    overdue: items.filter(i => i.status !== 'received' && (i.days_out ?? 0) >= 3).length,
    awaiting: items.filter(i => i.status === 'sent_back' || i.status === 'completed').length,
  }), [items]);

  const renderReceiveStrip = (item) => (
    <div className="flex flex-wrap items-end gap-2">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
        Receive back from {item.destination_name}
      </span>
      <label className="flex flex-1 flex-col gap-0.5" style={{ minWidth: '16rem' }}>
        <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">Notes</span>
        <span className="rounded border border-slate-300 bg-white">
          <TextCell
            label="Receive notes"
            value={receiveNotes}
            onChange={setReceiveNotes}
            placeholder="Optional — shortfall, damage, who handed it over"
          />
        </span>
      </label>
      <Button
        size="sm"
        className="h-7 bg-green-600 text-white hover:bg-green-700"
        disabled={submitting}
        onClick={() => confirmReceive(item)}
        data-testid="confirm-receive-btn"
      >
        <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
        {submitting ? 'Saving…' : 'Received'}
      </Button>
      <Button size="sm" variant="ghost" className="h-7"
        onClick={() => { setReceiving(null); setReceiveNotes(''); }}>
        <X className="h-3.5 w-3.5" />
      </Button>
      <span className="w-full text-[11px] text-slate-500">
        {item.order_display_id} · {item.stage_name} · {item.quantity} pcs — moves to the next production stage.
      </span>
    </div>
  );

  const rows = useMemo(() => {
    const out = [];
    for (const item of items) {
      out.push(item);
      if (receiving === item.id) {
        out.push({ id: `${item.id}:receive`, __full: true, __render: () => renderReceiveStrip(item) });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, receiving, receiveNotes, submitting]);

  const columns = useMemo(() => ([
    {
      key: 'order_display_id',
      header: 'Order',
      width: '9.5rem',
      render: (item) => <span className="block truncate font-medium text-brand-blue">{item.order_display_id}</span>,
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(11rem,1.2fr)',
      render: (item) => (
        <>
          <span className="block truncate font-medium text-slate-700">{item.customer_name}</span>
          {item.firm_name && <span className="block truncate text-[11px] leading-tight text-slate-400">{item.firm_name}</span>}
        </>
      ),
    },
    {
      key: 'product',
      header: 'Product',
      width: 'minmax(10rem,1fr)',
      render: (item) => (
        <span className="block truncate text-[12px] text-slate-600">
          {item.order_type}{(item.item_name || item.product_subtype) ? ` — ${item.item_name || item.product_subtype}` : ''}
        </span>
      ),
    },
    {
      key: 'stage_name',
      header: 'Stage',
      width: '8.5rem',
      render: (item) => <span className="block truncate text-[12px] text-slate-600">{item.stage_name}</span>,
    },
    {
      key: 'destination_name',
      header: 'Destination',
      width: '10rem',
      render: (item) => (
        <span className="flex min-w-0 items-center gap-1.5">
          {item.destination_type === 'city_office'
            ? <Building2 className="h-3.5 w-3.5 shrink-0 text-blue-500" />
            : <MapPin className="h-3.5 w-3.5 shrink-0 text-orange-500" />}
          <span className="truncate text-[12px] text-slate-600">{item.destination_name}</span>
        </span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (item) => <span className="font-medium text-slate-700">{item.quantity?.toLocaleString()}</span>,
      total: (list) => list
        .filter(r => !r.__full)
        .reduce((sum, r) => sum + (r.quantity || 0), 0)
        .toLocaleString(),
    },
    {
      key: 'sent_at',
      header: 'Sent',
      width: '6.5rem',
      render: (item) => (
        <span className="whitespace-nowrap text-[11px] text-slate-500">
          {item.sent_at ? formatIST(item.sent_at, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'days_out',
      header: 'Days',
      align: 'right',
      width: '5rem',
      render: (item) => (item.days_out ?? 0) >= 3 ? (
        <span className="inline-flex items-center gap-1 font-bold text-red-600">
          <AlertTriangle className="h-3.5 w-3.5" />{item.days_out}d
        </span>
      ) : (
        <span className="text-slate-500">{item.days_out ?? 0}d</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      render: (item) => (
        <span className={`text-[12px] font-medium ${STATUS_TEXT[item.status] || 'text-slate-500'}`}>
          {STATUS_LABELS[item.status] || item.status}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '7rem',
      align: 'right',
      render: (item) => (item.status === 'sent_back' || item.status === 'completed') && receiving !== item.id ? (
        <CellButton
          icon={CheckCircle2}
          label="Receive"
          tone="good"
          onClick={() => { setReceiving(item.id); setReceiveNotes(''); }}
          testId={`receive-btn-${item.id}`}
        />
      ) : null,
    },
  ]), [receiving]);

  // one card per item below lg; the same receive call, the same ledger
  if (isMobile) {
    return (
      <PhoneTracker
        view={view} onView={setView} items={items} total={total} loading={loading}
        onSearch={(text) => { setSearch(text); setPage(1); }} onRefresh={fetchItems}
        destinations={DESTINATIONS} destination={destination}
        onDestination={(key) => { setDestination(key); setPage(1); }}
        statusLabels={STATUS_LABELS} status={status}
        onStatus={(key) => { setStatus(key); setPage(1); }}
        counts={counts}
        page={page} totalPages={Math.ceil(total / 50)} onPage={setPage}
        receiving={receiving} notes={receiveNotes} onNotes={setReceiveNotes}
        onReceive={(item) => { setReceiving(item.id); setReceiveNotes(''); }}
        onCancelReceive={() => { setReceiving(null); setReceiveNotes(''); }}
        onConfirmReceive={() => confirmReceive(items.find(i => i.id === receiving))}
        submitting={submitting}
        ledger={<JobWorkLedger />}
      />
    );
  }

  return (
    <Screen data-testid="external-processing-page">
      <CommandBar
        title="External Processing"
        subtitle={view === 'tracker' ? `${total} item${total === 1 ? '' : 's'} tracked` : 'contractor charges and reconciliation'}
        actions={[
          {
            key: 'tracker',
            label: 'Tracker',
            icon: MapPin,
            tone: view === 'tracker' ? 'primary' : 'default',
            onClick: () => setView('tracker'),
          },
          {
            key: 'ledger',
            label: 'Job Work Ledger',
            icon: IndianRupee,
            tone: view === 'ledger' ? 'primary' : 'default',
            onClick: () => setView('ledger'),
          },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchItems, disabled: loading },
        ]}
      >
        {view === 'tracker' && (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="search-input"
              className="h-7 pl-8 text-xs"
              placeholder="Order ID, customer, vendor…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        )}
      </CommandBar>

      {view === 'ledger' ? (
        <Pane title="Job Work Ledger" bodyClassName="p-3">
          <JobWorkLedger />
        </Pane>
      ) : (
        <>
          <StatStrip
            stats={[
              { key: null, label: 'out', value: total, tone: 'blue' },
              { key: 'city_office', label: 'City Office', value: counts.city_office },
              { key: 'vendor', label: 'Vendors', value: counts.vendor },
              { key: null, label: 'over 3 days', value: counts.overdue, tone: counts.overdue ? 'danger' : 'default' },
              { key: null, label: 'awaiting receipt', value: counts.awaiting, tone: counts.awaiting ? 'warn' : 'default' },
            ]}
            activeKey={destination}
            onSelect={(key) => {
              if (key === 'city_office' || key === 'vendor') { setDestination(key); setPage(1); }
              else { setDestination(null); setPage(1); }
            }}
          >
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => { setStatus(status === value ? null : value); setPage(1); }}
                aria-pressed={status === value}
                data-testid={`filter-status-${value}`}
                className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                  status === value
                    ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {label}
              </button>
            ))}
          </StatStrip>

          <Pane
            title="Out of the building"
            aside={`${items.length} shown`}
            scroll={false}
            bodyClassName="flex flex-col"
            actions={total > 50 && (
              <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
                {page} / {Math.ceil(total / 50)}
                <button type="button" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
              </span>
            )}
          >
            <DataGrid
              data-testid="tracker-table"
              columns={columns}
              rows={rows}
              loading={loading}
              getRowId={(item) => item.id}
              showTotals={items.length > 0}
              rowClassName={(item) =>
                item.color === 'red' ? 'border-l-4 border-l-red-400 bg-red-50/40'
                  : item.color === 'green' ? 'border-l-4 border-l-emerald-400 bg-emerald-50/30' : ''
              }
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <Package className="h-8 w-8 opacity-50" />
                  No items in external processing
                </span>
              }
            />
          </Pane>
        </>
      )}
    </Screen>
  );
}
